import React from "react";
import styled from "styled-components";

// App-wide safety net. React error boundaries catch errors thrown during the
// render/lifecycle of any component below them. Without one, a single render
// crash blanks the whole admin app (white screen in production). Instead we
// show a friendly recovery card with a Reload button so the recruiter is never
// stranded — e.g. when they return to a long-idle tab and a stale request or
// expired session throws while re-rendering.
//
// Note: error boundaries do NOT catch async errors (promise rejections,
// setTimeout). The benign reCAPTCHA "Timeout" is handled separately by the
// global listeners in index.js.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log for diagnostics; never surface raw stack traces to the user.
    console.error("ErrorBoundary caught an error:", error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Wrap>
          <Card>
            <Title>Something went wrong</Title>
            <Text>
              This page ran into a problem. If you’ve been away for a while,
              your session may have timed out. Reloading usually fixes it.
            </Text>
            <ReloadButton type="button" onClick={this.handleReload}>
              Reload page
            </ReloadButton>
          </Card>
        </Wrap>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;

/* ── styles ─────────────────────────────────────────────────────────────── */
const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #faf9f6;
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  padding: 28px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h1`
  font-size: 1.4rem;
  font-weight: 800;
  color: #1b2a4a;
  margin: 0 0 10px 0;
`;

const Text = styled.p`
  color: #5a6b85;
  font-size: 0.98rem;
  line-height: 1.5;
  margin: 0 0 20px 0;
`;

const ReloadButton = styled.button`
  border: none;
  background: #1565C0;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.98rem;
  padding: 11px 22px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: #155cb0;
  }
`;
